/**
 * Générateur — Définir la notion de nombre positif, strictement positif,
 * négatif, strictement négatif.
 * Attendu ID : 912 | Niveau : 5ème | Domaine : Nombres relatifs
 *
 * Libellé officiel (spécification 912.json) : « Définir la notion de nombre
 * positif, strictement positif, négatif, strictement négatif. »
 *
 * Plan de cartes (version conforme à la spécification, campagne 5e 2026-09-22) :
 *
 *   Carte « apparier » — Relier chaque mot à son inégalité (niveaux 1 et 2)
 *     relier : les QUATRE qualificatifs, les quatre inégalités, les deux
 *     colonnes brassées indépendamment (le composant mélange les cibles, le
 *     générateur mélange les sources) ; niveau 1 = toutes les inégalités
 *     écrites « le nombre comparé à zéro », niveau 2 = sens varié d'une
 *     étiquette à l'autre, au moins deux sens différents ;
 *     qcm_unique : l'inégalité qui traduit un qualificatif, les trois autres
 *     en distracteurs, toutes écrites dans le MÊME sens (deux sens mêlés
 *     donneraient deux options équivalentes) ;
 *     texte_trou_drag : la définition en mots, deux trous, les deux autres
 *     qualificatifs en étiquettes pièges.
 *
 *   Carte « classer » — Les quatre mots pour un nombre (niveaux 3 et 4)
 *     classer : trois catégories dont les NOMS posent les quatre qualificatifs
 *     ensemble, celle de 0 disant sa double appartenance ;
 *     qcm_multiple : tous les mots qui conviennent à un nombre donné.
 *
 *   Carte « strict » — Strictement ou pas ? (niveaux 3 et 4)
 *     tableau_vf : quatre lignes tirées parmi six gabarits à prédicat variable ;
 *     qcm_multiple : les nombres strictement positifs (ou négatifs) d'une
 *     liste, 0 en piège, compte annoncé.
 *
 *   Carte « affirmation » — Vrai, faux, contre-exemple (niveau 5)
 *     tableau_vf : cinq gabarits d'affirmations générales à prédicat variable ;
 *     qcm_unique : le nombre qui montre qu'une affirmation fausse l'est,
 *     l'unicité du contre-exemple étant garantie par construction.
 *
 * Spécification (912.json) :
 *   Bornes appliquées : qualificatifs_imposes = liste fermée de quatre
 *     (`origine: prescrit`), toujours posés ensemble ;
 *     symboles_de_comparaison = <, >, ⩽, ⩾ ;
 *     sens_d_ecriture_de_l_inegalite = les deux sens (le nombre comparé à
 *     zéro / zéro comparé au nombre) ;
 *     statut_de_zero = 0 est à la fois positif et négatif, ni strictement
 *     positif ni strictement négatif — écrit dans CHAQUE consigne où 0 peut
 *     apparaître ; nombre_a_classer -20..20 à 0 ou 1 décimale ;
 *     nombre_d_etiquettes_a_relier = 4 exactement ;
 *     nombre_de_nombres_a_classer 4..8 (4 ou 5 retenus, voir « Non tranché ») ;
 *     part_de_questions_portant_sur_zero 0,2..0,33 (0 est l'un des 4 ou 5
 *     nombres du classement, et il est tiré une fois sur quatre dans les
 *     formats qui portent sur un seul nombre).
 *   Exclusions : aucun cinquième qualificatif (les étiquettes pièges sont
 *     toujours prises dans la liste fermée) ; aucune écriture détournée de
 *     l'inégalité (le nombre est comparé à zéro, et rien d'autre) ; aucune
 *     question qui isolerait « positif » sans « strictement positif » ; aucun
 *     nombre à plus d'une décimale ; dispositions d'étiquettes distinctes
 *     (4! ordres de sources × 2^4 sens) ; colonnes brassées indépendamment.
 *   Erreurs visées → mots-clés (_meta.params.distracteurs[].code,
 *   _meta.params.lignes[].code, _meta.params.paires[].code, erreurVisee) :
 *     « Reconnaître la forme d'un symbole sans en lire le sens »
 *         → symbole_lu_sans_sens (apparier_qcm_unique, apparier_relier)
 *     « Confondre large et strict, et déclarer 0 strictement positif »
 *         → large_et_strict_confondus (apparier_qcm_unique, classer_qcm_multiple,
 *           strict_vf, strict_qcm_multiple, affirmation_vf, affirmation_qcm_unique)
 *     « Croire que 0 n'est ni positif ni négatif »
 *         → zero_sans_signe (classer_qcm_multiple, strict_vf, affirmation_vf,
 *           affirmation_qcm_unique)
 *     « Classer un décimal négatif d'après sa distance à zéro »
 *         → classe_par_distance_a_zero (classer_qcm_multiple,
 *           strict_vf, affirmation_vf)
 *     « Prendre négatif pour inférieur à tout nombre positif »
 *         → negatif_confondu_avec_plus_petit (strict_vf, affirmation_vf,
 *           affirmation_qcm_unique)
 *     « Répondre au hasard dans un appariement »
 *         → appariement_au_hasard (apparier_relier : chaque paire porte le code
 *           de l'erreur qu'elle matérialise, matière première de la rétroaction
 *           par paire demandée par la spec)
 *   Graduation → cartes :
 *     niveau 1 → apparier (sens direct), apparier_texte_trou_drag ;
 *     niveau 2 → apparier (sens varié) ; niveau 3 → classer et strict
 *     (tirages entiers) ; niveau 4 → classer et strict (tirages décimaux) ;
 *     niveau 5 → affirmation.
 *   Non tranché : la rétroaction par paire n'est pas rendue par l'application ;
 *     nombre_de_nombres_a_classer (4..8) et part_de_questions_portant_sur_zero
 *     (0,2..0,33) sont incompatibles au-delà de 5 nombres ; le composant de
 *     classement ne place chaque étiquette qu'une fois, la double appartenance
 *     de 0 passe donc par le nom de sa catégorie et par le qcm_multiple.
 *
 * Convention enseignée : positif = ⩾ 0, négatif = ⩽ 0 ; « strictement » exclut 0.
 * Signe « - » = tiret clavier partout.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeClasser, makeRelier, makeTexteTrou,
    filtrerDistracteurs, tex,
} from '../../../utils';

const ATTENDU_ID = 912;

const L_APPARIER = 'Relier chaque mot à son inégalité';
const L_CLASSER = 'Les quatre mots pour un nombre';
const L_STRICT = 'Strictement ou pas ?';
const L_AFFIRMATION = 'Vrai, faux, contre-exemple';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** La convention sur 0, écrite dans la consigne (points_de_vigilance). */
const RAPPEL_ZERO = 'Rappel : 0 est à la fois positif et négatif, mais il n\'est '
    + 'ni strictement positif ni strictement négatif.';

/** Valeurs manipulées en DIXIÈMES entiers : dix(-45) = « -4,5 », dix(0) = « 0 ». */
const dix = (d) => {
    const s = d < 0 ? '-' : '';
    const a = Math.abs(d);
    return s + (a % 10 === 0 ? String(a / 10) : `${Math.floor(a / 10)},${a % 10}`);
};

/** Valeur absolue tirée dans nombre_a_classer : 1..20, 0 ou 1 décimale. */
const tirerAbsClasser = () => {
    if (randomChoice([true, false])) return randInt(1, 20) * 10;
    let k = randInt(1, 199);
    if (k % 10 === 0) k += 1;
    return k;
};
/** Un nombre à classer ; 0 est tiré VOLONTAIREMENT une fois sur quatre. */
const tirerNombre = () => {
    if (randInt(1, 4) === 1) return 0;
    const a = tirerAbsClasser();
    return randomChoice([true, false]) ? a : -a;
};

/**
 * n nombres à classer : zéro, puis n-1 valeurs absolues distinctes dont les
 * signes alternent (au moins un strictement positif et un strictement négatif).
 */
const tirerNombresAClasser = (n) => {
    const vus = new Set([0]);
    const abs = [];
    for (let essai = 0; essai < 80 && abs.length < n - 1; essai++) {
        const a = tirerAbsClasser();
        if (!vus.has(a)) { vus.add(a); abs.push(a); }
    }
    [30, 45, 120, 7, 168, 90].forEach((a) => { // repli déterministe, dans les bornes
        if (abs.length < n - 1 && !vus.has(a)) { vus.add(a); abs.push(a); }
    });
    return shuffle([0, ...abs.map((a, i) => (i % 2 === 0 ? a : -a))]);
};

/** Tire nb vérités avec au moins une vraie et une fausse. */
const tirerVerites = (nb) => {
    const v = [];
    for (let i = 0; i < nb; i++) v.push(randomChoice([true, false]));
    if (v.every(x => x)) v[randInt(0, nb - 1)] = false;
    else if (v.every(x => !x)) v[randInt(0, nb - 1)] = true;
    return v;
};

// ═════════ Les quatre qualificatifs (liste fermée, origine : prescrit) ═════════

const QUALIFS = [
    { id: 'positif', mot: 'positif', pluriel: 'positifs', strict: false, positif: true },
    { id: 'strict_positif', mot: 'strictement positif', pluriel: 'strictement positifs', strict: true, positif: true },
    { id: 'negatif', mot: 'négatif', pluriel: 'négatifs', strict: false, positif: false },
    { id: 'strict_negatif', mot: 'strictement négatif', pluriel: 'strictement négatifs', strict: true, positif: false },
];

/**
 * L'inégalité qui traduit un qualificatif.
 * `sensDirect` : le nombre comparé à zéro (n ⩾ 0) ; sinon zéro comparé au
 * nombre (0 ⩽ n). Les deux écritures disent la même chose.
 */
const LETTRES_INEGALITE = ['a', 'n', 't', 'x', 'y'];

const inegalite = (q, sensDirect, lettre) => {
    const direct = q.positif ? (q.strict ? '>' : '\\geqslant') : (q.strict ? '<' : '\\leqslant');
    const inverse = q.positif ? (q.strict ? '<' : '\\leqslant') : (q.strict ? '>' : '\\geqslant');
    return sensDirect ? tex(`${lettre} ${direct} 0`) : tex(`0 ${inverse} ${lettre}`);
};

/** Le qualificatif vérifié par un nombre : toujours DEUX des quatre. */
const qualifsDe = (v) => (v > 0
    ? ['positif', 'strict_positif']
    : v < 0 ? ['negatif', 'strict_negatif'] : ['positif', 'negatif']);

/** L'erreur d'élève qu'un qualificatif faux matérialise pour le nombre v. */
const codeFauxQualif = (v) => {
    if (v === 0) return 'large_et_strict_confondus';           // 0 déclaré strictement positif/négatif
    if (v % 10 !== 0) return 'classe_par_distance_a_zero';     // décimal rangé d'après sa distance à zéro
    return 'symbole_lu_sans_sens';
};

// ═════════ Carte « apparier » — Relier chaque mot à son inégalité ═════════

const genApparierRelier = () => {
    const niveau2 = randomChoice([true, false]);
    const sens = QUALIFS.map(() => (niveau2 ? randomChoice([true, false]) : true));
    // Niveau 2 : au moins deux sens différents, sinon la série retombe au niveau 1.
    if (niveau2 && sens.every(s => s === sens[0])) sens[randInt(0, 3)] = !sens[0];
    const lettre = randomChoice(LETTRES_INEGALITE);
    const paires = shuffle(QUALIFS.map((q, i) => ({
        source: `un nombre ${q.mot}`,
        target: inegalite(q, sens[i], lettre),
        // Le code nomme l'erreur que cette paire matérialise : matière de la
        // rétroaction par paire demandée par la spécification.
        code: sens[i] ? 'large_et_strict_confondus' : 'symbole_lu_sans_sens',
    })));
    const payload = makeRelier(
        paires.map(p => ({ source: p.source, target: p.target })),
        `<p><b>Relie chaque mot à l'inégalité qui le traduit.</b> ${RAPPEL_ZERO}</p>`,
        opts(L_APPARIER));
    payload._meta.params = {
        niveau: niveau2 ? 2 : 1, sens, lettre, paires, erreurVisee: 'appariement_au_hasard',
    };
    return payload;
};

const genApparierQcm = () => {
    // Un seul sens d'écriture par question : mêler les deux sens donnerait deux
    // options équivalentes (0 < n et n > 0 disent la même chose).
    const sensDirect = randomChoice([true, false]);
    const cible = randomChoice(QUALIFS);
    const lettre = randomChoice(LETTRES_INEGALITE);
    const distracteurs = QUALIFS.filter(q => q.id !== cible.id).map(q => ({
        valeur: inegalite(q, sensDirect, lettre),
        // Même signe, strictesse différente : large et strict confondus.
        // Signe différent : le symbole lu sans son sens.
        code: q.positif === cible.positif ? 'large_et_strict_confondus' : 'symbole_lu_sans_sens',
    }));
    const correct = inegalite(cible, sensDirect, lettre);
    const payload = makeQcm([correct, ...distracteurs.map(d => d.valeur)], correct,
        `<p><b>Quelle inégalité traduit « un nombre ${cible.mot} » ?</b> ${RAPPEL_ZERO}</p>`,
        opts(L_APPARIER));
    payload._meta.params = { cible: cible.id, sensDirect, lettre, niveau: sensDirect ? 1 : 2, distracteurs };
    return payload;
};

const DEFINITIONS = {
    positif: 'Un nombre supérieur ou égal à 0 est un nombre',
    strict_positif: 'Un nombre strictement plus grand que 0 est un nombre',
    negatif: 'Un nombre inférieur ou égal à 0 est un nombre',
    strict_negatif: 'Un nombre strictement plus petit que 0 est un nombre',
};

const genApparierTexteTrou = () => {
    // Deux trous seulement : les deux autres qualificatifs, écrits en clair
    // dans les lignes non trouées, deviennent les étiquettes pièges (la liste
    // des qualificatifs est fermée, aucune étiquette n'en sort).
    const troues = [randomChoice(['positif', 'strict_positif']), randomChoice(['negatif', 'strict_negatif'])];
    const ordre = shuffle(QUALIFS.map(q => q.id));
    const phrase = ordre.map((id) => {
        const q = QUALIFS.find(x => x.id === id);
        return `${DEFINITIONS[id]} ${troues.includes(id) ? `{{${q.mot}}}` : q.mot}.`;
    }).join('\n');
    const candidats = QUALIFS.filter(q => !troues.includes(q.id)).map(q => q.mot);
    const distracteurs = filtrerDistracteurs(phrase, candidats);
    const payload = makeTexteTrou(phrase,
        `<p><b>Complète chaque définition avec le mot qui convient.</b> ${RAPPEL_ZERO}</p>`,
        { ...opts(L_APPARIER), mode: 'drag', distracteurs });
    payload._meta.params = {
        troues, niveau: 1,
        distracteurs: distracteurs.map(d => ({ valeur: d, code: 'large_et_strict_confondus' })),
    };
    return payload;
};

// ═════════ Carte « classer » — Les quatre mots pour un nombre ═════════

// Les trois catégories du classement NOMMENT les quatre qualificatifs : aucune
// ne peut être remplie en ignorant la distinction large / strict (exclusion
// « aucune question qui isolerait positif sans strictement positif »). Le
// composant de classement ne place chaque étiquette qu'une fois — la double
// appartenance de 0 est portée par le NOM de sa catégorie, et le format
// qcm_multiple de la même carte la fait produire par l'élève.
const CAT_POS = 'Positif, et même strictement positif';
const CAT_NEG = 'Négatif, et même strictement négatif';
const CAT_ZERO = 'Positif et négatif, sans être strictement positif ni strictement négatif';

const genClasserQuatre = () => {
    // 4 ou 5 nombres : zéro est alors l'un d'eux, soit 20 à 25 % des nombres
    // de la série (part_de_questions_portant_sur_zero 0,2..0,33).
    const nbNombres = randInt(4, 5);
    const vals = tirerNombresAClasser(nbNombres);
    const categories = [
        { name: CAT_POS, items: vals.filter(v => v > 0).map(dix) },
        { name: CAT_NEG, items: vals.filter(v => v < 0).map(dix) },
        { name: CAT_ZERO, items: vals.filter(v => v === 0).map(dix) },
    ];
    const payload = makeClasser(categories,
        `<p><b>Place chaque nombre dans la catégorie qui le décrit entièrement.</b> ${RAPPEL_ZERO}</p>`,
        opts(L_CLASSER));
    payload._meta.params = {
        vals, nbNombres, niveau: vals.every(v => v % 10 === 0) ? 3 : 4,
        erreurVisee: 'zero_sans_signe',
    };
    return payload;
};

const genClasserQcmMulti = () => {
    const v = tirerNombre();
    const bons = qualifsDe(v);
    const corrects = QUALIFS.filter(q => bons.includes(q.id));
    const faux = QUALIFS.filter(q => !bons.includes(q.id));
    const payload = makeQcm(QUALIFS.map(q => q.mot), corrects.map(q => q.mot),
        `<p><b>Coche les deux mots qui conviennent au nombre ${dix(v)}.</b> ${RAPPEL_ZERO}</p>`,
        opts(L_CLASSER), true);
    payload._meta.params = {
        v, niveau: v % 10 === 0 ? 3 : 4,
        distracteurs: faux.map(q => ({ valeur: q.mot, code: codeFauxQualif(v) })),
        ...(v === 0 ? { erreurVisee: 'zero_sans_signe' } : {}),
    };
    return payload;
};

// ═════════ Carte « strict » — Strictement ou pas ? ═════════

/**
 * Gabarits à prédicat variable : chaque gabarit produit une ligne VRAIE ou
 * FAUSSE selon `v`. ctx : { neg (négatif entier), dec (décimal négatif),
 * pos (positif entier), seuil (entier strictement positif) }.
 */
const GABARITS_STRICT = [
    {
        id: 'strict_du_negatif', code: 'symbole_lu_sans_sens',
        ligne: (v, c) => `${dix(c.neg)} est strictement ${v ? 'négatif' : 'positif'}.`,
    },
    {
        id: 'zero_large', code: 'zero_sans_signe',
        ligne: (v) => (v ? 'Le nombre 0 est positif.' : 'Le nombre 0 n\'est ni positif ni négatif.'),
    },
    {
        id: 'zero_strict', code: 'large_et_strict_confondus',
        ligne: (v) => `Le nombre 0 ${v ? 'n\'est pas' : 'est'} strictement positif.`,
    },
    {
        id: 'decimal_negatif', code: 'classe_par_distance_a_zero',
        ligne: (v, c) => `${dix(c.dec)} est un nombre ${v ? 'négatif' : 'positif'}.`,
    },
    {
        id: 'seuil', code: 'negatif_confondu_avec_plus_petit',
        ligne: (v, c) => `Un nombre plus petit que ${v ? 0 : dix(c.seuil)} est forcément négatif.`,
    },
    {
        id: 'positif_strict', code: 'large_et_strict_confondus',
        ligne: (v, c) => `${dix(c.pos)} est ${v ? 'positif et strictement positif' : 'positif sans être strictement positif'}.`,
    },
];

const genStrictVf = () => {
    const ctx = {
        neg: -tirerAbsClasser(),
        dec: -(randInt(1, 19) * 10 + randInt(1, 9)),
        pos: tirerAbsClasser(),
        seuil: randInt(1, 20) * 10,
    };
    const gabarits = shuffle(GABARITS_STRICT).slice(0, 4);
    const verites = tirerVerites(4);
    const lignes = gabarits.map((g, i) => ({
        template: g.id, code: g.code, vrai: verites[i], texte: g.ligne(verites[i], ctx),
    }));
    const payload = makeVraiFaux(
        lignes.map(l => l.texte),
        lignes.map(l => (l.vrai ? 0 : 1)),
        `<p><b>Pour chaque affirmation, coche Vrai ou Faux.</b> ${RAPPEL_ZERO}</p>`,
        opts(L_STRICT));
    payload._meta.params = { ...ctx, niveau: 3, lignes };
    return payload;
};

/**
 * ⚠️ La consigne NE DIT PLUS combien de nombres il faut cocher.
 *
 * « Coche les DEUX nombres qui sont strictement positifs » dispensait l'élève de la
 * seule décision que la carte évalue : celle qui porte sur 0. Il cochait les deux
 * nombres sans signe moins, atteignait le compte annoncé, et n'avait jamais à se
 * demander si 0 est strictement positif (retour prof 2026-09-25). Le piège du large et
 * du strict était desamorcé par l'énoncé lui-même.
 */

const genStrictQcmMulti = () => {
    const surPositifs = randomChoice([true, false]);
    const k = randomChoice([2, 3]);
    const vals = tirerNombresAClasser(6).filter(v => v !== 0);
    const positifs = vals.filter(v => v > 0);
    const negatifs = vals.filter(v => v < 0);
    const cibles = (surPositifs ? positifs : negatifs).slice(0, k);
    const autres = (surPositifs ? negatifs : positifs).slice(0, 5 - k);
    // Repli déterministe : compléter avec des valeurs hors des absolues tirées.
    const utilisees = new Set(vals.map(Math.abs));
    [15, 65, 110, 190, 35].forEach((a) => {
        if (cibles.length < k && !utilisees.has(a)) { utilisees.add(a); cibles.push(surPositifs ? a : -a); }
        else if (autres.length < 5 - k && !utilisees.has(a)) { utilisees.add(a); autres.push(surPositifs ? -a : a); }
    });
    const choix = [...cibles.map(dix), ...autres.map(dix), '0']; // 0 : le piège du large / strict
    const payload = makeQcm(choix, cibles.map(dix),
        `<p><b>Coche TOUS les nombres qui sont strictement ${surPositifs ? 'positifs' : 'négatifs'}.</b> `
        + `${RAPPEL_ZERO}</p>`,
        opts(L_STRICT), true);
    payload._meta.params = {
        cibles, surPositifs, niveau: [...cibles, ...autres].every(v => v % 10 === 0) ? 3 : 4,
        distracteurs: [
            { valeur: '0', code: 'large_et_strict_confondus' },
            ...autres.map(v => ({ valeur: dix(v), code: 'symbole_lu_sans_sens' })),
        ],
    };
    return payload;
};

// ═════════ Carte « affirmation » — Vrai, faux, contre-exemple ═════════

const GABARITS_AFFIRMATION = [
    {
        id: 'implication_positif', code: 'large_et_strict_confondus',
        ligne: v => (v
            ? 'Tout nombre strictement positif est positif.'
            : 'Tout nombre positif est strictement positif.'),
    },
    {
        id: 'implication_negatif', code: 'large_et_strict_confondus',
        ligne: v => (v
            ? 'Tout nombre strictement négatif est négatif.'
            : 'Tout nombre négatif est strictement négatif.'),
    },
    {
        id: 'seuil', code: 'negatif_confondu_avec_plus_petit',
        ligne: (v, c) => `Tout nombre plus petit que ${v ? 0 : dix(c.seuil)} est négatif.`,
    },
    {
        id: 'zero_deux', code: 'zero_sans_signe',
        ligne: v => (v
            ? 'Le nombre 0 est à la fois positif et négatif.'
            : 'Le nombre 0 n\'est ni positif ni négatif.'),
    },
    {
        id: 'distance', code: 'classe_par_distance_a_zero',
        ligne: (v, c) => (v
            ? `Le nombre ${dix(c.dec)} est négatif.`
            : `Le nombre ${dix(c.dec)} est positif, car ${dix(-c.dec)} est positif.`),
    },
];

const genAffirmationVf = () => {
    const ctx = {
        seuil: randInt(1, 20) * 10,
        dec: -(randInt(1, 19) * 10 + randInt(1, 9)),
    };
    const gabarits = shuffle(GABARITS_AFFIRMATION).slice(0, 4);
    const verites = tirerVerites(4);
    const lignes = gabarits.map((g, i) => ({
        template: g.id, code: g.code, vrai: verites[i], texte: g.ligne(verites[i], ctx),
    }));
    const payload = makeVraiFaux(
        lignes.map(l => l.texte),
        lignes.map(l => (l.vrai ? 0 : 1)),
        `<p><b>Pour chaque affirmation, coche Vrai ou Faux.</b> ${RAPPEL_ZERO}</p>`,
        opts(L_AFFIRMATION));
    payload._meta.params = { ...ctx, niveau: 5, lignes };
    return payload;
};

/**
 * Contre-exemple d'une affirmation fausse. Chaque type est construit pour que
 * UNE SEULE des quatre propositions vérifie l'hypothèse sans vérifier la
 * conclusion : le contre-exemple est unique par construction.
 */
const genAffirmationQcm = () => {
    const type = randomChoice(['positif_strict', 'negatif_strict', 'seuil_haut', 'seuil_bas']);
    const seuil = randInt(2, 19) * 10;
    const dedans = randInt(1, seuil - 1); // 0 < dedans < seuil, en dixièmes
    const dehors = seuil + randInt(1, Math.max(1, 200 - seuil));
    let affirmation;
    let correct;
    let distracteurs;
    if (type === 'positif_strict') {
        affirmation = 'Tout nombre positif est strictement positif.';
        correct = 0; // seul nombre positif qui n'est pas strictement positif
        distracteurs = [
            { v: dedans, code: 'large_et_strict_confondus' },
            { v: -dedans, code: 'symbole_lu_sans_sens' },
            { v: -seuil, code: 'symbole_lu_sans_sens' },
        ];
    } else if (type === 'negatif_strict') {
        affirmation = 'Tout nombre négatif est strictement négatif.';
        correct = 0;
        distracteurs = [
            { v: -dedans, code: 'large_et_strict_confondus' },
            { v: dedans, code: 'symbole_lu_sans_sens' },
            { v: seuil, code: 'symbole_lu_sans_sens' },
        ];
    } else if (type === 'seuil_haut') {
        affirmation = `Tout nombre plus petit que ${dix(seuil)} est négatif.`;
        correct = dedans;     // positif et plus petit que le seuil
        distracteurs = [
            { v: 0, code: 'zero_sans_signe' },          // 0 est négatif : pas un contre-exemple
            { v: -dedans, code: 'negatif_confondu_avec_plus_petit' },
            { v: dehors, code: 'negatif_confondu_avec_plus_petit' },
        ];
    } else {
        affirmation = `Tout nombre plus grand que ${dix(-seuil)} est positif.`;
        correct = -dedans;    // négatif et plus grand que le seuil
        distracteurs = [
            { v: 0, code: 'zero_sans_signe' },          // 0 est positif : pas un contre-exemple
            { v: dedans, code: 'negatif_confondu_avec_plus_petit' },
            { v: -dehors, code: 'negatif_confondu_avec_plus_petit' },
        ];
    }
    const payload = makeQcm([dix(correct), ...distracteurs.map(d => dix(d.v))], dix(correct),
        `<p><b>L'affirmation « ${affirmation} » est fausse. Quel nombre le montre ?</b> ${RAPPEL_ZERO}</p>`,
        opts(L_AFFIRMATION));
    payload._meta.params = {
        type, affirmation, correct, seuil, niveau: 5,
        distracteurs: distracteurs.map(d => ({ valeur: dix(d.v), code: d.code })),
    };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    apparier_relier: genApparierRelier,
    apparier_qcm_unique: genApparierQcm,
    apparier_texte_trou_drag: genApparierTexteTrou,
    classer_quatre_classer: genClasserQuatre,
    classer_qcm_multiple: genClasserQcmMulti,
    strict_vf: genStrictVf,
    strict_qcm_multiple: genStrictQcmMulti,
    affirmation_vf: genAffirmationVf,
    affirmation_qcm_unique: genAffirmationQcm,
};

const FORMAT_LABELS = {
    relier: 'Relier', qcm_unique: 'QCM', texte_trou_drag: 'Drag',
    quatre_classer: 'Classer', qcm_multiple: 'QCM Multi', vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'apparier', label: L_APPARIER, formatsList: buildFormats('apparier') },
    { id: 'classer', label: L_CLASSER, formatsList: buildFormats('classer') },
    { id: 'strict', label: L_STRICT, formatsList: buildFormats('strict') },
    { id: 'affirmation', label: L_AFFIRMATION, formatsList: buildFormats('affirmation') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
